import { Add, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Grid,
  Divider as MuiDivider,
  Paper,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridRowEditStopReasons } from "@mui/x-data-grid";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";
import Filters from "./Filters";
import { Data } from "./Data";
import DoughnutChart from "../rcAuto/DoughnutChart";
import BarChart from "../rcAuto/BarChart";
import { useGetRcTableColumns } from "../rcAuto/columns";
import QuoteForm from "./QuoteForm";
import { DEFAULT_GAP_LIMIT } from "./data/satellitare";
import { API_BASE_URL } from "../../../config";
import {
  RecuperoTargaDialog,
  useRecuperoTarga,
  ESITO_RECUPERO_TARGA,
} from "../rcautoDetailsProfile2/lib/RecuperoTarga";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

function CVT() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [filtersActive, setFiltersActive] = useState(isDesktop);

  const {
    handlePageChange,
    handlePageSizeChange,
    handleApplyFilters,
    handleRemoveFilters,
    doughnutChartData,
    barChartData,
    rcData,
    totalRows,
    loading,
    filtersLoading,
    isApplyFiltersButtonDisabled,
    pageNumber,
    rowsPerPage,
    filterOptions,
    filterData,
    setFilterData,
    setFilterByDate,
    canClearFilters,
    isRemovingFilters,
    dateFilterNumber,
    setDateFilterNumber,
    lastUpdateTime,
    refreshList,
  } = Data();

  const navigate = useNavigate();

  // il cliente porta al dettaglio della pratica come la targa: la provenienza
  // serve al dettaglio per mostrare il form di riquotazione CVT
  const handleClienteClick = (row) => {
    navigate(`/pages/rcAutoDetails/${row.idUID}`, {
      state: { fromPath: "/pages/cvt" },
    });
  };

  const columns = useGetRcTableColumns("cvt", null, handleClienteClick, null, {
    showReboot: false,
  });
  const dispatch = useDispatch();
  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  const initialFormData = {
    cliente: "",
    targa: "",
    marca: "",
    modello: "",
    provinceCode: "",
    province: "",
    vehicleValue: "",
    duration: "24",
    gapLimit: DEFAULT_GAP_LIMIT,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { recuperoTarga, tentativoRecupero, attendiRecuperoTarga } =
    useRecuperoTarga();

  const buildRequestData = () => ({
    data: {
      cliente: formData.cliente.trim(),
      targa: formData.targa.trim(),
      marca: formData.marca.trim(),
      modello: formData.modello.trim(),
      provincia: formData.province,
      provinceCode: formData.provinceCode,
      vehicleValue: parseFloat(formData.vehicleValue),
      duration: parseInt(formData.duration, 10),
      // massimale GAP scelto: serve alla function per quotare la sola opzione
      // richiesta invece di tutte quelle disponibili
      coverageLimit: parseInt(formData.gapLimit, 10),
      createdAt: new Date().toISOString(),
    },
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cliente?.trim()) {
      newErrors.cliente = "Inserisci un cliente valido";
    }
    if (!formData.provinceCode) {
      newErrors.province = "Seleziona la provincia";
    }
    if (!formData.vehicleValue || parseFloat(formData.vehicleValue) <= 0) {
      newErrors.vehicleValue = "Inserisci un valore veicolo valido";
    }
    if (!formData.duration) {
      newErrors.duration = "Seleziona la durata";
    }
    if (!formData.gapLimit) {
      newErrors.gapLimit = "Seleziona il massimale GAP";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateQuote = async () => {
    const token = sessionStorage.getItem("token");
    if (!validateForm()) {
      handleSnackbar("Ci sono errori nel form", "error");
      return;
    }
    setIsQuoteLoading(true);
    try {
      // un'API per pacchetto (4 chiamate in parallelo) + quella del prodotto
      // aggiuntivo, che quota satellitare e GAP per massimale e durata
      const payload = buildRequestData();
      const response = await fetch(`${API_BASE_URL}/api/dealerQuotation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (responseData.status === -1) {
        handleSnackbar(responseData.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
        return;
      }
      if (responseData.status === 1) {
        const idSubmission = responseData.data.idSubmission;
        const apriDettaglio = () =>
          navigate(`/pages/rcAutoDetails/${idSubmission}`, {
            state: { fromPath: "/pages/cvt" },
          });

        setIsEditing(false);
        if (!formData.targa.trim()) {
          apriDettaglio();
          return;
        }

        // con la targa si aspetta che il server recuperi i dati del veicolo:
        // se non arrivano entro i tentativi si ricarica la lista
        setIsQuoteLoading(false);
        const esito = await attendiRecuperoTarga(idSubmission);
        if (esito === ESITO_RECUPERO_TARGA.PRONTO) {
          apriDettaglio();
        } else if (esito === ESITO_RECUPERO_TARGA.ESAURITO) {
          handleSnackbar(
            "I dati della targa non sono ancora disponibili.",
            "warning",
          );
          refreshList();
        }
      }
    } finally {
      setIsQuoteLoading(false);
    }
  };

  const handleAddQuote = () => {
    setFormData(initialFormData);
    setErrors({});
    setIsEditing(true);
  };

  const handleBackToList = () => {
    setIsEditing(false);
  };

  const switchFilterActive = () => {
    setFiltersActive((prev) => !prev);
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    return updatedRow;
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Helmet title="CVT" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h3" display="inline">
                CVT
              </Typography>
              <Typography fontSize={11} display="inline">
                Ultimo aggiornamento:{" "}
                {lastUpdateTime.toLocaleDateString("it-IT", {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="end" gap={4}>
              <Button
                mr={2}
                variant="contained"
                color="primary"
                onClick={handleAddQuote}
              >
                <Add />
              </Button>
            </Box>
          </Box>
          <Divider my={4} />
          <Grid container>
            {/* Charts */}
            <Grid
              container
              wrap="wrap"
              sx={{
                margin: isDesktop ? "0 -16px" : "0",
                padding: "0",
                width: isDesktop ? "calc(100% + 16px)" : "100%",
              }}
              spacing={isDesktop ? 4 : 0}
            >
              <Grid item lg={4} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
                <DoughnutChart
                  doughnutChartData={doughnutChartData}
                  filterOptions={filterOptions}
                  setFilterByDate={setFilterByDate}
                  dateFilterNumber={dateFilterNumber}
                  setDateFilterNumber={setDateFilterNumber}
                />
              </Grid>
              <Grid item lg={8} md={6} sm={12} xs={12} sx={{ flex: 1 }}>
                <BarChart
                  barChartData={barChartData}
                  filterOptions={filterOptions}
                  setFilterByDate={setFilterByDate}
                  dateFilterNumber={dateFilterNumber}
                  setDateFilterNumber={setDateFilterNumber}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <>
                <Card sx={{ marginBottom: "1rem" }}>
                  <Filters
                    filtersActive={filtersActive}
                    switchFilterActive={switchFilterActive}
                    handleApplyFilters={handleApplyFilters}
                    handleRemoveFilters={handleRemoveFilters}
                    filtersLoading={filtersLoading}
                    isApplyFiltersButtonDisabled={isApplyFiltersButtonDisabled}
                    filterOptions={filterOptions}
                    filterData={filterData}
                    setFilterData={setFilterData}
                    canClearFilters={canClearFilters}
                    isRemovingFilters={isRemovingFilters}
                  />
                </Card>

                <Card mb={6}>
                  <Paper>
                    <Box sx={{ width: "100%" }}>
                      <DataGrid
                        autoHeight
                        pageSize={rowsPerPage}
                        onPageSizeChange={handlePageSizeChange}
                        rowsPerPageOptions={[5, 10, 20]}
                        pagination
                        paginationMode="server"
                        rowCount={totalRows}
                        page={pageNumber - 1}
                        onPageChange={(params) => handlePageChange(params)}
                        getRowId={(usersList) => usersList.idUID}
                        rows={rcData}
                        columns={columns}
                        editMode="row"
                        allowUpdating={true}
                        experimentalFeatures={{ newEditingApi: true }}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        disableColumnMenu
                        disableSelectionOnClick
                        localeText={{ noRowsLabel: "Non ci sono risultati" }}
                        sx={{
                          "& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus":
                            {
                              outline: "none",
                            },
                          "& .MuiDataGrid-footerContainer": {
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px",
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Card>
              </>
            </Grid>
          </Grid>

          <RecuperoTargaDialog
            open={recuperoTarga}
            tentativo={tentativoRecupero}
          />

          <Dialog
            open={isEditing}
            onClose={handleBackToList}
            fullWidth
            maxWidth="md"
            scroll="paper"
          >
            <DialogTitle>Nuovo preventivo</DialogTitle>
            <DialogContent dividers sx={{ pt: 3 }}>
              <QuoteForm
                formData={formData}
                setFormData={setFormData}
                onCalculate={calculateQuote}
                onClose={handleBackToList}
                isLoading={isQuoteLoading}
                errors={errors}
                submitLabel="Ricerca"
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}

export default CVT;
